import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  CircularProgress,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import { toast } from "react-hot-toast";
import CustomText, { textStyles } from "../../../../components/CustomText";
import { useTranslation } from "react-i18next";

const PayExpenseModal = ({ open, onClose, expense, onSuccess }) => {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDate, setPaymentDate] = useState(dayjs());
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofUrl, setPaymentProofUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  // Reset form when modal closes
  React.useEffect(() => {
    if (!open) {
      setNote("");
      setPaymentMethod("");
      setPaymentDate(dayjs());
      setPaymentProofFile(null);
      setPaymentProofUrl(null);
      setError(null);
    }
  }, [open]);

  // Early return after all hooks
  if (!expense) return null;

  const compactFieldLabelSx = {
    fontSize: "13px",
    fontWeight: 500,
    color: "#44546F",
    mb: 0.5,
    display: "block",
    fontFamily: "Poppins, sans-serif",
  };

  const compactInputSx = {
    fontSize: "13px",
    fontFamily: "Poppins, sans-serif",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "13px",
      minHeight: 40,
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "13px",
      py: "8px",
    },
    "& .MuiSelect-select": {
      fontSize: "13px",
      py: "7px",
      minHeight: "unset",
    },
    "& .MuiInputLabel-root": {
      fontSize: "13px",
    },
    "& .MuiInputLabel-shrink": {
      fontSize: "12px",
    },
  };

  /** MUI X v8 DatePicker uses PickersTextField / PickersOutlinedInput, not MuiOutlinedInput. */
  const compactPickersDateFieldSx = {
    fontFamily: "Poppins, sans-serif",
    "& .MuiPickersOutlinedInput-root": {
      fontSize: "13px",
      borderRadius: "8px",
      minHeight: 40,
      pl: "10px",
      pr: "6px",
    },
    "& .MuiPickersOutlinedInput-input": {
      fontSize: "13px",
      lineHeight: 1.43,
      paddingTop: "6px",
      paddingBottom: "6px",
    },
    "& .MuiPickersSectionList-root": {
      fontSize: "13px",
      lineHeight: 1.43,
    },
    "& .MuiPickersSectionList-section": {
      fontSize: "13px",
      lineHeight: 1.43,
    },
    "& .MuiPickersSectionList-sectionContent": {
      fontSize: "13px",
      lineHeight: 1.43,
    },
    "& .MuiIconButton-root": {
      padding: "4px",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.125rem",
    },
  };

  /** Shrinks the calendar popup (header label, weekday labels, day cells) to match the compact field. */
  const compactCalendarPopperSx = {
    "& .MuiPickersCalendarHeader-root": {
      minHeight: 32,
      paddingLeft: "8px",
      paddingRight: "4px",
    },
    "& .MuiPickersCalendarHeader-label": {
      fontSize: "13px",
    },
    "& .MuiPickersCalendarHeader-switchViewButton": {
      padding: "2px",
    },
    "& .MuiPickersArrowSwitcher-button": {
      padding: "4px",
    },
    "& .MuiDayCalendar-weekDayLabel": {
      fontSize: "12px",
      width: 32,
      height: 32,
    },
    "& .MuiPickersDay-root": {
      fontSize: "12px",
      width: 32,
      height: 32,
    },
    "& .MuiPickersYear-yearButton": {
      fontSize: "13px",
    },
  };

  const selectMenuPaperSx = {
    "& .MuiMenuItem-root": {
      fontSize: "13px",
      minHeight: 36,
      py: 0.5,
      fontFamily: "Poppins, sans-serif",
    },
  };

  // Format date helper
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return "";
    switch (currencyCode.toUpperCase()) {
      case "USD":
        return "$";
      case "EUR":
        return "€";
      default:
        return "";
    }
  };

  // Handle file upload - Using the correct endpoint from documentation
  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      // Validate file type (JPEG, PNG, PDF)
      const isImage = file.type.startsWith("image/") && 
        ["image/jpeg", "image/png"].includes(file.type);
      const isPdf = file.type === "application/pdf";

      if (!isImage && !isPdf) {
        throw new Error("Only JPEG, PNG images or PDF files are allowed");
      }

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error("File size must be less than 10MB");
      }

      const formData = new FormData();
      formData.append("file", file);

      const uploadUrl = `${BASE_URL}/company-admin/expenses/upload-payment-proof`;

      console.log("📤 Uploading payment proof to:", uploadUrl);

      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
        body: formData,
      });

      const data = await res.json();
      console.log("📥 Server response:", data);

      if (!res.ok || data.error) {
        throw new Error(data?.message || "Upload failed");
      }

      const fileUrl = data?.data?.fileUrl || data?.fileUrl || "";
      console.log("✅ Uploaded file URL:", fileUrl);

      if (!fileUrl) {
        throw new Error("No file URL returned from server");
      }

      setPaymentProofUrl(fileUrl);
      setPaymentProofFile(file);
      toast.success(
        t("reimbursementRequest.payModal.uploadSuccess") ||
          "Payment proof uploaded successfully"
      );
    } catch (err) {
      console.error("❌ Upload failed:", err);
      setError(err.message || "Upload failed");
      toast.error(err.message || "Failed to upload payment proof");
    } finally {
      setUploading(false);
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Handle camera capture
  const handleCameraCapture = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = "environment"; // Use back camera on mobile
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleFileUpload(file);
      }
    };
    input.click();
  };

  // Handle pay expense
  const handlePay = async () => {
    setPaying(true);
    setError(null);

    try {
      // Format payment date as YYYY-MM-DD
      const formattedDate = paymentDate 
        ? paymentDate.format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');

      // Validate date is not in the future
      if (paymentDate && paymentDate.isAfter(dayjs(), 'day')) {
        throw new Error("Payment date cannot be in the future");
      }

      const endpoint = `${BASE_URL}/company-admin/expenses/${expense.id}/pay`;
      
      const payload = {
        paid_at: formattedDate,
        payment_method: paymentMethod || null,
        note: note.trim() || null,
        attachment_url: paymentProofUrl || null,
      };

      console.log("📤 Paying expense:", endpoint, payload);

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${decryptedToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      console.log("📥 Pay response:", data);

      if (!res.ok || data.error) {
        throw new Error(data.message || "Failed to pay expense");
      }

      toast.success(
        data.message ||
          t("reimbursementRequest.paySuccess") ||
          "Expense paid successfully"
      );

      if (onSuccess) {
        onSuccess(data.data);
      }

      onClose();
    } catch (err) {
      console.error("❌ Pay expense failed:", err);
      setError(err.message || "Failed to pay expense");
      toast.error(err.message || "Failed to pay expense");
    } finally {
      setPaying(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          fontSize: "18px",
        }}
      >
        <CustomText
          sx={{
            ...textStyles.h1,
            fontSize: 20,
            fontWeight: 600,
            color: "#2C384C",
          }}
        >
          {t("reimbursementRequest.payModal.title") || "Pay Expense"}
        </CustomText>
        <IconButton onClick={() => onClose()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Read-only Expense Details */}
        <Box
          sx={{
            border: "1px solid #E4E6EF",
            borderRadius: 2,
            p: 2,
            mb: 3,
            backgroundColor: "#F8F9FA",
          }}
        >
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 16,
              fontWeight: 600,
              color: "#2C384C",
              mb: 2,
            }}
          >
            {t("reimbursementRequest.payModal.expenseDetails") ||
              "Expense Details"}
          </CustomText>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Employee */}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#5E5C5C",
                }}
              >
                {t("reimbursementRequest.payModal.employee") || "Employee"}:
              </CustomText>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 14,
                  fontWeight: 400,
                  color: "#172B4D",
                }}
              >
                {expense.worker?.name || "-"}
              </CustomText>
            </Box>

            {/* Description */}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#5E5C5C",
                }}
              >
                {t("reimbursementRequest.payModal.description") ||
                  "Description"}:
              </CustomText>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 14,
                  fontWeight: 400,
                  color: "#172B4D",
                  textAlign: "right",
                  maxWidth: "60%",
                }}
              >
                {expense.description || "-"}
              </CustomText>
            </Box>

            {/* Date */}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#5E5C5C",
                }}
              >
                {t("reimbursementRequest.payModal.date") || "Date"}:
              </CustomText>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 14,
                  fontWeight: 400,
                  color: "#172B4D",
                }}
              >
                {formatDateTime(expense.date_of_expense)}
              </CustomText>
            </Box>

            {/* Amount */}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#5E5C5C",
                }}
              >
                {t("reimbursementRequest.payModal.amount") || "Amount"}:
              </CustomText>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#006EC2",
                }}
              >
                {getCurrencySymbol(expense.currency)} {expense.amount}
              </CustomText>
            </Box>
          </Box>
        </Box>

        {/* Optional Fields */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Payment Date */}
          <Box>
            <Box component="label" sx={compactFieldLabelSx}>
              {t("reimbursementRequest.payModal.paymentDate") ||
                "Payment Date"} *
            </Box>
            <DatePicker
              value={paymentDate}
              onChange={(newValue) => setPaymentDate(newValue)}
              maxDate={dayjs()}
              format="YYYY-MM-DD"
              formatDensity="dense"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  sx: {
                    ...compactInputSx,
                    ...compactPickersDateFieldSx,
                  },
                },
                popper: {
                  sx: compactCalendarPopperSx,
                },
              }}
            />
          </Box>

          {/* Payment Note */}
          <Box>
            <Box component="label" sx={compactFieldLabelSx}>
              {t("reimbursementRequest.payModal.note") ||
                "Payment Note (Optional)"}
            </Box>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="project_input"
              style={{
                width: "100%",
                minHeight: "72px",
                padding: "8px 10px",
                fontSize: "13px",
                lineHeight: 1.45,
                fontFamily: "Poppins, sans-serif",
                borderRadius: "8px",
                boxSizing: "border-box",
              }}
              placeholder={
                t("reimbursementRequest.payModal.notePlaceholder") ||
                "Add a note about this payment..."
              }
            />
          </Box>

          {/* Payment Method */}
          <Box>
            <TextField
              select
              size="small"
              fullWidth
              label={
                t("reimbursementRequest.payModal.paymentMethod") ||
                "Payment Method (Optional)"
              }
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: selectMenuPaperSx,
                  },
                },
              }}
              sx={compactInputSx}
            >
              <MenuItem value="" dense sx={{ fontSize: "13px", minHeight: 36 }}>
                {t("reimbursementRequest.payModal.selectPaymentMethod") ||
                  "Select payment method"}
              </MenuItem>
              <MenuItem value="cash" dense sx={{ fontSize: "13px", minHeight: 36 }}>
                {t("reimbursementRequest.payModal.paymentMethodCash") ||
                  "Cash"}
              </MenuItem>
              <MenuItem value="bank" dense sx={{ fontSize: "13px", minHeight: 36 }}>
                {t("reimbursementRequest.payModal.paymentMethodBank") ||
                  "Bank Transfer"}
              </MenuItem>
              <MenuItem value="transfer" dense sx={{ fontSize: "13px", minHeight: 36 }}>
                {t("reimbursementRequest.payModal.paymentMethodTransfer") ||
                  "Wire Transfer"}
              </MenuItem>
              <MenuItem value="other" dense sx={{ fontSize: "13px", minHeight: 36 }}>
                {t("reimbursementRequest.payModal.paymentMethodOther") ||
                  "Other"}
              </MenuItem>
            </TextField>
          </Box>

          {/* Payment Proof */}
          <Box>
            <Box component="label" sx={compactFieldLabelSx}>
              {t("reimbursementRequest.payModal.paymentProof") ||
                "Supporting Proof (Optional)"}
            </Box>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <Button
                component="label"
                variant="outlined"
                disabled={uploading || paying}
                size="small"
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "#E4E6EF",
                  color: "#666",
                  fontSize: "13px",
                  py: 0.5,
                  "&:hover": {
                    borderColor: "#006EC2",
                    backgroundColor: "#F0F7FF",
                  },
                }}
              >
                {t("reimbursementRequest.payModal.uploadFile") || "Upload File"}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={handleFileChange}
                  disabled={uploading || paying}
                />
              </Button>
              <Button
                variant="outlined"
                onClick={handleCameraCapture}
                disabled={uploading || paying}
                size="small"
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "#E4E6EF",
                  color: "#666",
                  fontSize: "13px",
                  py: 0.5,
                  "&:hover": {
                    borderColor: "#006EC2",
                    backgroundColor: "#F0F7FF",
                  },
                }}
              >
                {t("reimbursementRequest.payModal.takePhoto") || "Take Photo"}
              </Button>
            </Box>
            {uploading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <CustomText
                  sx={{
                    ...textStyles.h1,
                    fontSize: 14,
                    color: "#666",
                  }}
                >
                  {t("reimbursementRequest.payModal.uploading") ||
                    "Uploading..."}
                </CustomText>
              </Box>
            )}
            {paymentProofUrl && !uploading && (
              <Box sx={{ mt: 1 }}>
                <CustomText
                  sx={{
                    ...textStyles.h1,
                    fontSize: 14,
                    color: "#4BCE97",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  ✓{" "}
                  {t("reimbursementRequest.payModal.uploadSuccess") ||
                    "Payment proof uploaded"}
                </CustomText>
                {paymentProofFile && (
                  <CustomText
                    sx={{
                      ...textStyles.h1,
                      fontSize: 12,
                      color: "#666",
                      mt: 0.5,
                    }}
                  >
                    {paymentProofFile.name}
                  </CustomText>
                )}
              </Box>
            )}
          </Box>

          {/* Error Message */}
          {error && (
            <Box
              sx={{
                p: 1.5,
                backgroundColor: "#FEE",
                border: "1px solid #FCC",
                borderRadius: 1,
              }}
            >
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 14,
                  color: "#D32F2F",
                }}
              >
                {error}
              </CustomText>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "flex-end", p: 2 }}>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Button
            onClick={() => onClose()}
            disabled={paying || uploading}
            variant="outlined"
            sx={{
              fontSize: 15,
              textTransform: "none",
              borderRadius: "5px",
              fontFamily: "Poppins, sans-serif",
              color: "#006EC2",
              border: "2px solid #006EC2",
              px: 3,
              py: 1,
              height: "42px",
            }}
          >
            {t("reimbursementRequest.payModal.cancel") || "Cancel"}
          </Button>

          <Button
            onClick={handlePay}
            disabled={paying || uploading}
            sx={{
              textTransform: "none",
              borderRadius: "5px",
              px: 3,
              py: 1,
              fontFamily: "Poppins, sans-serif",
              height: "42px",
              backgroundColor: "#006EC2",
              color: "white",
            }}
          >
            {paying && (
              <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
            )}
            {paying
              ? t("reimbursementRequest.payModal.processing") || "Processing..."
              : t("reimbursementRequest.payModal.confirmPayment") ||
                "Confirm Payment"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
    </LocalizationProvider>
  );
};

export default PayExpenseModal;
